import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  DigitalCase, DigitalQuestion, PartialBy, QuestionCaseSetting,
} from '../../../redux/types';
import GenericDialog from '../../GenericDialog';
import ApiClient from '../../../loopback/apiClient';
import SurveyOptionContext from '../../../views/admin/SurveyOptionContext';

type Props = {
  digitalSurveyId: number;
  selectedSectionId?: number;
  digitalQuestions: DigitalQuestion[];
  digitalCases: DigitalCase[];
  settingsByQuestionAndCase: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined; }
  addQuestionCaseSetting: (qcs: Omit<QuestionCaseSetting, 'id'>) => void;
  updateQuestionCaseSetting: (qcs: QuestionCaseSetting) => void;
  bulkSettingsAllCasesPerQuestion: (payload: Omit<QuestionCaseSetting, 'id' | 'digitalCaseId'>) => void;
  bulkSettingsAllQuestionsPerCase: (payload: Omit<QuestionCaseSetting, 'id' | 'digitalQuestionId'>) => void;
  deleteGTByQuestionAndCase: (questionId: number, caseId: number) => void;
  fetchDigitalGroundTruths: (surveyId: number) => void;
};

type QuestionCaseSettingDialogProps = {
  settingBuffer: PartialBy<QuestionCaseSetting, 'id'> | undefined;
  onChangeSetting: (settingId: keyof Omit<QuestionCaseSetting, 'id' | 'digitalSurveyId' | 'digitalQuestionId' | 'digitalCaseId' | 'digitalSectionId'>, checked: boolean) => void;
  onCloseDialog: () => void;
  onSaveSetting: () => void;
}

/**
 * Question Case Dialog Component
 */
const QuestionCaseSettingDialog: React.FC<QuestionCaseSettingDialogProps> = ({
  settingBuffer,
  onChangeSetting,
  onCloseDialog,
  onSaveSetting,
}: QuestionCaseSettingDialogProps) => (
  <GenericDialog
    maxWidth="xs"
    isOpen={!!settingBuffer}
    onRequestClose={onCloseDialog}
  >
    <Box sx={{ m: 2 }}>
      <Typography variant="h5">Question Case Setting</Typography>
      <Box sx={{ m: 2 }}>
        <FormGroup>
          <FormControlLabel checked={settingBuffer?.hide || false} onChange={(e, c: boolean) => { onChangeSetting('hide', c); }} control={<Checkbox />} label="Hide Question" />
          <FormControlLabel disabled={settingBuffer?.hide} checked={settingBuffer?.compulsory || false} onChange={(e, c: boolean) => { onChangeSetting('compulsory', c); }} control={<Checkbox />} label="Compulsory Question" />
        </FormGroup>
      </Box>
      <Divider />
      {
        settingBuffer?.hide && (
          <Alert sx={{ mt: 2 }} severity="warning">All the Groundtruths for this case will be deleted</Alert>
        )
      }
      <Box sx={{ mt: 2 }} justifyContent="flex-end" display="flex">
        <Button onClick={onSaveSetting} variant="contained">Save</Button>
      </Box>
    </Box>
  </GenericDialog>
);

/**
 * Bulk Setting Dialog Component
 */
type BulkSettingsDialogProps = {
  selectedQuestion?: DigitalQuestion;
  selectedCase?: DigitalCase;
  sectionId: number;
  surveyId: number;
  onCloseDialog: () => void;
  digitalQuestions: DigitalQuestion[];
  digitalCases: DigitalCase[];
  settingsByQuestionAndCase: Record<number, Record<number, QuestionCaseSetting> | undefined>;
  bulkSettingsAllCasesPerQuestion: (payload: Omit<QuestionCaseSetting, 'id' | 'digitalCaseId'>) => void;
  bulkSettingsAllQuestionsPerCase: (payload: Omit<QuestionCaseSetting, 'id' | 'digitalQuestionId'>) => void;
  fetchDigitalGroundTruths: (surveyId: number) => void;
}

const BulkSettingsDialog: React.FC<BulkSettingsDialogProps> = ({
  selectedCase,
  selectedQuestion,
  sectionId,
  surveyId,
  digitalQuestions,
  digitalCases,
  settingsByQuestionAndCase,
  onCloseDialog,
  bulkSettingsAllCasesPerQuestion,
  bulkSettingsAllQuestionsPerCase,
  fetchDigitalGroundTruths,
}: BulkSettingsDialogProps) => {
  const [compulsoryStatus, setCompulsoryStatus] = useState<'all' | 'none' | 'intermediate'>('intermediate');
  const [dismissStatus, setDismissStatus] = useState<'all' | 'none' | 'intermediate'>('intermediate');

  // checkbox default status when question or case is clicked
  useEffect(() => {
    const getCheckboxStatus = (checkArr: boolean[], totalRefArray: unknown[]) => {
      if ((checkArr.includes(true) && checkArr.includes(false))
        || (checkArr.length !== totalRefArray.length && checkArr.includes(true))) {
        return 'intermediate';
      } if (checkArr.length === 0 || !checkArr.includes(true)) {
        return 'none';
      }
      return 'all';
    };
    if (selectedQuestion) {
      const settingEntries = digitalCases
        .map((dc) => settingsByQuestionAndCase?.[selectedQuestion.id]?.[dc.id])
        .filter((s) => (s !== undefined));
      const compulsoryChecks = settingEntries.map((s) => !!s?.compulsory);
      const dismissChecks = settingEntries.map((s) => !!s?.hide);
      setCompulsoryStatus(getCheckboxStatus(compulsoryChecks, digitalCases));
      setDismissStatus(getCheckboxStatus(dismissChecks, digitalCases));
    } else if (selectedCase) {
      const settingEntries = digitalQuestions
        .map((dq) => settingsByQuestionAndCase?.[dq.id]?.[selectedCase.id])
        .filter((s) => (s !== undefined));
      const compulsoryChecks = settingEntries.map((s) => !!s?.compulsory);
      const dismissChecks = settingEntries.map((s) => !!s?.hide);
      setCompulsoryStatus(getCheckboxStatus(compulsoryChecks, digitalQuestions));
      setDismissStatus(getCheckboxStatus(dismissChecks, digitalQuestions));
    }
  }, [digitalCases, digitalQuestions, selectedCase, selectedQuestion, settingsByQuestionAndCase]);

  const onCompulsoryChange = (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    setCompulsoryStatus(checked ? 'all' : 'none');
  };

  const onDismissChange = (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    setDismissStatus(checked ? 'all' : 'none');
    if (checked) {
      setCompulsoryStatus('none');
    }
  };

  const onSave = () => {
    if (compulsoryStatus === 'intermediate' && dismissStatus === 'intermediate') {
      onCloseDialog();
      setCompulsoryStatus('intermediate');
      setDismissStatus('intermediate');
      return;
    }
    if (selectedQuestion) {
      // update settings for all case against a question
      const settingobj: Omit<QuestionCaseSetting, 'digitalCaseId' | 'id'> = {
        digitalQuestionId: selectedQuestion.id,
        digitalSectionId: sectionId,
        digitalSurveyId: surveyId,
        compulsory: compulsoryStatus === 'all',
        hide: dismissStatus === 'all',
      };
      bulkSettingsAllCasesPerQuestion(settingobj);
      if (dismissStatus === 'all') {
        // delete all groundtruths
        ApiClient.deleteDigitalGroundTruthBy({
          digitalQuestionId: selectedQuestion.id,
        }).then(() => fetchDigitalGroundTruths(surveyId));
      }
    } else if (selectedCase) {
      // update settings for all questions against a case
      const settingobj: Omit<QuestionCaseSetting, 'digitalQuestionId' | 'id'> = {
        digitalCaseId: selectedCase.id,
        digitalSectionId: sectionId,
        digitalSurveyId: surveyId,
        compulsory: compulsoryStatus === 'all',
        hide: dismissStatus === 'all',
      };
      bulkSettingsAllQuestionsPerCase(settingobj);
      if (dismissStatus === 'all') {
        // delete all groundtruths
        ApiClient.deleteDigitalGroundTruthBy({
          digitalCaseId: selectedCase.id,
        }).then(() => fetchDigitalGroundTruths(surveyId));
      }
    }
    onCloseDialog();
    setCompulsoryStatus('intermediate');
  };

  return (
    <GenericDialog
      maxWidth="xs"
      isOpen={!!selectedQuestion || !!selectedCase}
      onRequestClose={onCloseDialog}
    >
      <Box sx={{ m: 2 }}>
        <Typography variant="h5">
          {
            selectedCase !== undefined ? 'Settings for Case' : 'Settings for Question'
          }
        </Typography>
        <Typography color="GrayText">
          {selectedCase && selectedCase.name}
          {selectedQuestion && selectedQuestion.text}
        </Typography>
        <Box sx={{ mx: 2, my: 3 }}>
          <FormGroup>
            <FormControlLabel control={<Checkbox indeterminate={dismissStatus === 'intermediate'} checked={dismissStatus !== 'intermediate' && dismissStatus === 'all'} onChange={onDismissChange} />} label="Hide" />
            <FormControlLabel control={<Checkbox disabled={dismissStatus === 'all'} indeterminate={compulsoryStatus === 'intermediate'} checked={compulsoryStatus !== 'intermediate' && compulsoryStatus === 'all'} onChange={onCompulsoryChange} />} label="Compulsory" />
          </FormGroup>
        </Box>
        <Divider />
        {
          dismissStatus === 'all' && (
            <Alert sx={{ mt: 2 }} severity="warning">All corresponding Groundtruths will be deleted</Alert>
          )
        }
        <Box sx={{ mt: 2 }} justifyContent="flex-end" display="flex">
          <Button onClick={onSave} variant="contained">Save</Button>
        </Box>
      </Box>
    </GenericDialog>
  );
};

type ShowDisableSurveyEditAlertProps = {
  show: boolean;
  onClose: () => void;
};

const ShowDisableSurveyEditAlert: React.FC<ShowDisableSurveyEditAlertProps> = (
  { show, onClose }: ShowDisableSurveyEditAlertProps,
) => (
  <GenericDialog
    maxWidth="xs"
    isOpen={show}
    onRequestClose={onClose}
  >
    <Box component="span" sx={{ m: 2 }}>
      <Typography>
        <Alert severity="warning">
          Question Case Settings has been disabled
        </Alert>
      </Typography>
    </Box>
  </GenericDialog>
);

const clickablbeCell = {
  backgroundColor: 'ButtonFace',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
};

/**
 * Default Component
 */
const QuestionCaseSettingsGrid: React.FC<Props> = ({
  digitalSurveyId,
  selectedSectionId,
  digitalCases,
  digitalQuestions,
  settingsByQuestionAndCase,
  addQuestionCaseSetting,
  updateQuestionCaseSetting,
  bulkSettingsAllCasesPerQuestion,
  bulkSettingsAllQuestionsPerCase,
  deleteGTByQuestionAndCase,
  fetchDigitalGroundTruths,
}: Props) => {
  const [settingBuffer, setSettingBuffer] = useState<PartialBy<QuestionCaseSetting, 'id'>>();
  const [selectedQuestion, setSelectedQuestion] = useState<DigitalQuestion>();
  const [selectedCase, setSelectedCase] = useState<DigitalCase>();
  const [showDisableEditAlert, setShowDisableEditAlert] = useState<boolean>(false);
  const { disableEdit } = useContext(SurveyOptionContext);

  if (!selectedSectionId
    || !digitalQuestions.length || !digitalCases.length) {
    return null;
  }

  const onQuestionCaseClick = (dqId: number, dcId: number) => {
    const existingSetting = settingsByQuestionAndCase.get(dqId, dcId);
    if (existingSetting && existingSetting.id) {
      // update setting
      setSettingBuffer(existingSetting);
    } else {
      // add setting
      const newSetting: Omit<QuestionCaseSetting, 'id'> = {
        digitalCaseId: dcId,
        digitalQuestionId: dqId,
        digitalSectionId: selectedSectionId,
        digitalSurveyId,
        compulsory: false,
      };
      setSettingBuffer(newSetting);
    }
  };

  const onSaveQuestionCaseSetting = () => {
    if (!settingBuffer) {
      return;
    }
    if (!settingBuffer.id) {
      // no id add setting
      addQuestionCaseSetting(settingBuffer);
    } else if (settingBuffer.id !== undefined) {
      // id exists update setting
      updateQuestionCaseSetting(settingBuffer as QuestionCaseSetting);
    }
    if (settingBuffer.hide) {
      // delete groundtruth if question is dismissed (hidden)
      deleteGTByQuestionAndCase(settingBuffer.digitalQuestionId, settingBuffer.digitalCaseId);
    }
    setSettingBuffer(undefined);
  };

  const onCloseDialog = () => {
    setSettingBuffer(undefined);
    setSelectedQuestion(undefined);
    setSelectedCase(undefined);
    setShowDisableEditAlert(false);
  };

  const onChangeQuestionCaseSetting = (settingId: keyof Omit<QuestionCaseSetting, 'id' | 'digitalSurveyId' | 'digitalQuestionId' | 'digitalCaseId' | 'digitalSectionId'>, checked: boolean) => {
    if (settingBuffer === undefined) {
      return;
    }
    const newBuffer: PartialBy<QuestionCaseSetting, 'id'> = {
      ...settingBuffer,
      [settingId]: checked,
    };
    if (settingId === 'hide' && checked) {
      newBuffer.compulsory = false;
    }
    setSettingBuffer(newBuffer);
  };

  const onQuestionClick = (dq: DigitalQuestion) => {
    setSelectedQuestion(dq);
    setSelectedCase(undefined);
  };

  const onCaseClick = (dc: DigitalCase) => {
    setSelectedCase(dc);
    setSelectedQuestion(undefined);
  };

  return (
    <Box>
      <TableContainer>
        <Table sx={{ position: 'relative', overflow: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  backgroundColor: 'white',
                  left: 0,
                  minWidth: 300,
                }}
              >
                Questions
              </TableCell>
              {
                digitalCases.map((dc) => (
                  <TableCell
                    sx={{ '&:hover': clickablbeCell }}
                    key={dc.id}
                    align="center"
                    onClick={!disableEdit ? () => onCaseClick(dc)
                      : () => setShowDisableEditAlert(true)}
                  >
                    {dc.name}
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              digitalQuestions
                .map((dq) => (
                  <TableRow key={dq.id}>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        backgroundColor: 'white',
                        left: 0,
                        fontWeight: 500,
                        '&:hover': clickablbeCell,
                      }}
                      onClick={!disableEdit
                        ? () => onQuestionClick(dq) : () => setShowDisableEditAlert(true)}
                    >
                      {dq.text}
                    </TableCell>
                    {
                      digitalCases.map((dc) => (
                        <TableCell
                          key={dc.id}
                          align="center"
                          sx={{
                            lineBreak: 'strict',
                            minWidth: '155px',
                            '&:hover': clickablbeCell,
                          }}
                          onClick={!disableEdit
                            ? () => onQuestionCaseClick(dq.id, dc.id)
                            : () => setShowDisableEditAlert(true)}
                        >
                          {
                            (() => {
                              const setting = settingsByQuestionAndCase.get(dq.id, dc.id);
                              return (
                                <Box sx={setting?.hide ? { color: 'grey.400' } : null}>
                                  <Box display="flex" justifyContent="center" gap={1}>
                                    <Typography variant="body2" flexBasis={90}>
                                      dismiss
                                    </Typography>
                                    {
                                      setting && setting.hide ? <CheckIcon fontSize="small" color={setting.hide ? 'disabled' : 'primary'} /> : <CloseIcon fontSize="small" color={setting?.hide ? 'disabled' : 'secondary'} />
                                    }
                                  </Box>
                                  <Box display="flex" justifyContent="center" gap={1}>
                                    <Typography variant="body2" flexBasis={90}>
                                      <FormattedMessage id="questioneditor.required" />
                                    </Typography>
                                    {
                                      setting && setting.compulsory ? <CheckIcon fontSize="small" color={setting.hide ? 'disabled' : 'primary'} /> : <CloseIcon fontSize="small" color={setting?.hide ? 'disabled' : 'secondary'} />
                                    }
                                  </Box>
                                </Box>
                              );
                            })()
                          }
                        </TableCell>
                      ))
                    }
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </TableContainer>
      <QuestionCaseSettingDialog
        onCloseDialog={onCloseDialog}
        onChangeSetting={onChangeQuestionCaseSetting}
        onSaveSetting={onSaveQuestionCaseSetting}
        settingBuffer={settingBuffer}
      />
      <BulkSettingsDialog
        selectedCase={selectedCase}
        selectedQuestion={selectedQuestion}
        sectionId={selectedSectionId}
        surveyId={digitalSurveyId}
        digitalQuestions={digitalQuestions}
        digitalCases={digitalCases}
        settingsByQuestionAndCase={settingsByQuestionAndCase}
        onCloseDialog={onCloseDialog}
        bulkSettingsAllCasesPerQuestion={bulkSettingsAllCasesPerQuestion}
        bulkSettingsAllQuestionsPerCase={bulkSettingsAllQuestionsPerCase}
        fetchDigitalGroundTruths={fetchDigitalGroundTruths}
      />
      <ShowDisableSurveyEditAlert
        show={showDisableEditAlert}
        onClose={onCloseDialog}
      />
    </Box>
  );
};

export default QuestionCaseSettingsGrid;
